// Phase 85.A.2 — Redirect legacy `/athlete/profile/*` URLs sang `/u/:username`.
//
// Hỗ trợ 3 dạng URL cũ:
//   • /athlete/profile                 → /u/{me.username || me.id}
//   • /athlete/profile/:id             → /u/{username_resolved || id}
//   • /athlete/profile/:athleteId/timeline → /u/{username_resolved || athleteId}/timeline
//   • /athlete/profile/:athleteId/partners → /u/{username_resolved || athleteId}/partners
//
// Khi `:id` là username thì backend `/api/public/athletes/{id}` đã resolve
// được — chỉ cần forward thẳng. Khi `:id` là user_id thì cần fetch để biết
// username (nếu không có username thì dùng id làm fallback theo user choice Z).
import { useEffect } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

/** /athlete/profile → /u/{me.username || me.id} (logged in) hoặc / (guest).
 *
 * Phase 85.A.7 — Guest redirect về trang chủ thay vì login (user phản hồi: link
 * chia sẻ profile public không nên ép guest đăng nhập, để trải nghiệm mở hơn).
 *
 * Phase 85.A.7-fix — Wait for AuthContext.loading hết trước khi quyết định.
 * Trước fix: race condition khi `user` chưa load xong → tưởng guest → redirect
 * "/" → HomeRouter thấy admin role → bounce sang /admin/dashboard. Sau fix:
 * loading=true render spinner; loading=false + có user → /u/:slug đúng.
 */
export function LegacyMyProfileRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center text-white/55 text-[12px]">
        Đang chuyển hướng...
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  const slug = user.username || user.id;
  return <Navigate to={`/u/${slug}`} replace />;
}

/**
 * /athlete/profile/:id[/suffix] → /u/{slug}[/suffix]
 *
 * Backend resolve username/id giống nhau, nhưng để URL "đẹp" ta cố gắng
 * dùng username. Nếu lookup fail (404 / network) → fallback dùng raw param.
 */
export function LegacyProfileRedirect({ suffix = "" }) {
  const { id, athleteId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const raw = id || athleteId;

  useEffect(() => {
    if (!raw) return;
    let cancelled = false;
    api.get(`/public/athletes/${raw}`).then((r) => {
      if (cancelled) return;
      const slug = r.data?.username || r.data?.id || raw;
      navigate(`/u/${slug}${suffix}${location.search}${location.hash}`, { replace: true });
    }).catch(() => {
      if (cancelled) return;
      navigate(`/u/${raw}${suffix}${location.search}${location.hash}`, { replace: true });
    });
    return () => { cancelled = true; };
  }, [raw, suffix, location.search, location.hash, navigate]);

  return (
    <div className="min-h-screen bg-[#0B1220] flex items-center justify-center text-white/55 text-[12px]">
      Đang chuyển hướng...
    </div>
  );
}
